import Image from 'next/image'
import Link from 'next/link'

// Suporta tanto snake_case (ArticlesApi) quanto camelCase (Highlights)
interface Specialist {
  id: number
  slug: string
  title: string
  qty_articles?: number
  editorial: { name: string } | { slug: string; name?: string } | string
  short_content?: string
  shortContent?: string
  published_at?: string
  publishedAt?: string
  time_to_read?: number
  timeToRead?: number
  thumbnail_image?: { path: string; alt: string }
  thumbnailImage?: { path: string; alt: string }
  author?: {
    name: string
    photo?: string
    avatar?: string
    slug?: string
    path?: string
  }
  authors?: Array<{ name: string; path?: string; avatar?: string }>
  path: string
}

interface SpecialistsSectionProps {
  title: string
  link: string
  textLink: string
  qtyArticles?: number
  specialistsArticles: Array<Specialist>
}

export const SpecialistsSection: React.FC<SpecialistsSectionProps> = ({
  title,
  link,
  textLink,
  qtyArticles,
  specialistsArticles
}) => {
  if (!specialistsArticles || specialistsArticles.length === 0) {
    return null
  }

  return (
    <section className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-xl md:text-3xl font-bold">{title}</h2>
        <Link
          href={link}
          className="hover:bg-gray-100 rounded-full border px-2 py-1 text-gray-400"
        >
          {textLink}
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {specialistsArticles.slice(0, qtyArticles).map(article => {
          // Suporta tanto snake_case quanto camelCase
          const shortContent = article.short_content || article.shortContent
          const publishedAt = article.published_at || article.publishedAt
          const timeToRead = article.time_to_read || article.timeToRead
          const thumbnailImage =
            article.thumbnail_image || article.thumbnailImage

          // Suporta tanto author (singular) quanto authors (array)
          const authorPhoto =
            article.author?.photo ||
            article.author?.avatar ||
            article.authors?.[0]?.avatar
          const authorName = article.author?.name || article.authors?.[0]?.name

          // Pega o nome da editoria de diferentes estruturas
          const editorialName =
            typeof article.editorial === 'string'
              ? article.editorial
              : (article.editorial as any)?.name ||
                (article.editorial as any)?.slug ||
                'Especialista'

          return (
            <a
              key={article.id}
              href={article.path}
              className="cursor-pointer rounded-lg font-noto w-full h-full flex flex-col"
            >
              <div className="bg-white overflow-hidden h-full">
                {/* Foto do especialista */}
                <div className="relative h-48 bg-gradient-to-br from-pink-100 to-pink-200 rounded-lg">
                  <Image
                    src={authorPhoto || '/assets/default-specialist.jpg'}
                    alt={authorName || 'Especialista'}
                    fill
                    className="object-cover rounded-lg"
                  />
                </div>

                <div className="py-2 flex flex-col h-60 relative">
                  {/* Tags do especialista */}
                  <div className="flex items-center gap-2 mb-3 h-12">
                    {article.editorial && (
                      <span className="bg-green text-white px-2 py-1 rounded text-xs font-medium">
                        {editorialName}
                      </span>
                    )}
                    {authorName && (
                      <span className="bg-[#f1c786] text-[#444444] px-2 py-1 rounded text-xs font-medium">
                        {authorName}
                      </span>
                    )}
                  </div>

                  {/* Título do artigo */}
                  <h3 className="font-bold text-lg mb-4 leading-tight flex-grow h-24 overflow-y-hidden">
                    {article.title}
                  </h3>

                  {/* Descrição do artigo */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3 h-24 overflow-hidden">
                    {shortContent ||
                      'Saiba como escapar desse problema com nosso colunista e advogado Fred Boschin'}
                  </p>

                  {/* Data e tempo de leitura */}
                  <div className="flex justify-between text-xs text-[#CAC4D0] mt-auto">
                    <span>
                      {publishedAt
                        ? new Date(publishedAt).toLocaleDateString('pt-BR')
                        : ''}
                    </span>
                    <span>{timeToRead || 4} min de leitura</span>
                  </div>
                </div>
              </div>
            </a>
          )
        })}
      </div>
    </section>
  )
}
